import { Card } from "@/components/ui/card";
import { Calendar, User } from "lucide-react";

interface PrescriptionReceitaPreviewProps {
  patient: {
    full_name: string;
    data_nascimento?: string;
    cpf?: string;
  };
  prescriptionItems: Array<{
    prescription: string;
    dosage?: string;
    duration?: string;
    item_type?: string;
  }>;
  prescriptionDate?: string;
}

const itemTypeLabels: Record<string, string> = {
  medication: "Medicamento",
  supplement: "Suplemento",
  vitamin: "Vitamina",
  exam: "Exame",
  procedure: "Procedimento",
  orientation: "Orientação",
  other: "Outro",
};

const itemTypeIcons: Record<string, string> = {
  medication: "💊",
  supplement: "🌿",
  vitamin: "💛",
  exam: "🔬",
  procedure: "🏥",
  orientation: "📋",
  other: "📦",
};

export function PrescriptionReceitaPreview({
  patient,
  prescriptionItems,
  prescriptionDate,
}: PrescriptionReceitaPreviewProps) {
  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(patient.data_nascimento);

  return (
    <Card className="p-6 bg-card border-border">
      <div className="space-y-4">
        {/* Header com dados do paciente */}
        <div className="flex items-center gap-3 pb-4 border-b border-border">
          <User className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">{patient.full_name}</h3>
            <p className="text-sm text-muted-foreground">
              {age && `${age} anos`}
              {patient.cpf && (age ? ` • CPF: ${patient.cpf}` : `CPF: ${patient.cpf}`)}
            </p>
          </div>
          {prescriptionDate && (
            <div className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {new Date(prescriptionDate).toLocaleDateString("pt-BR")}
            </div>
          )}
        </div>

        {/* Lista de itens prescritos */}
        <div className="space-y-3">
          <h4 className="font-semibold text-sm text-foreground">Itens Prescritos</h4>
          {prescriptionItems.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border"
            >
              <span className="text-2xl flex-shrink-0">
                {itemTypeIcons[item.item_type || "medication"]}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{item.prescription}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {itemTypeLabels[item.item_type || "medication"]}
                    </p>
                  </div>
                </div>
                {(item.dosage || item.duration) && (
                  <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                    {item.dosage && <span>Dosagem: {item.dosage}</span>}
                    {item.duration && <span>Prazo: {item.duration}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
